import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import {
  mapRawSpecListVersionToSpecListVersion,
  mapRawToSpecList,
  mapRawToSpecListMinimal,
} from "@/mappers/specList";
import {
  ArchiveSpecList,
  CreateCustomSpecList,
  CreateMasterSpecList,
  CreateNewSpecListVersion,
  CreateProjectSpecList,
  CreateSpecList,
  RawSpecList,
  RawSpecListMinimal,
  RawSpecListVersion,
  SpecValue,
  UpdateMasterSpecList,
  UploadDesign,
} from "@/types/specList";
import {
  SWRApiError,
  swrConfigOptions,
  UpdaterParams,
  useFetcher,
  useMutater,
  useUpdater,
  useUploader,
  useUploaderWithResponse,
} from "./swr";
import { useMaskStructures } from "./useMasks";
import { useMemo } from "react";
import { useAuth } from "@/context/AuthContext";
import { settings } from "@/settings";
import { downloadFileByUrl } from "@/lib/utils";
import {
  UpdateDesignRequest,
  UploadDesignPreview,
} from "@/components/projects/steps/DesignUploadStep";
import { ValidationResponse } from "@/components/specLists/createSpecList/ValidationTable";

export const useSpecLists = () => {
  const fetcher = useFetcher();

  const { data = [], ...rest } = useSWR<RawSpecListMinimal[], SWRApiError>(
    { url: "/spec_lists" },
    fetcher,
    swrConfigOptions,
  );

  return { data: data.map(mapRawToSpecListMinimal), ...rest };
};

export const useFullSpecLists = () => {
  const fetcher = useFetcher();

  const { data = [], ...rest } = useSWR<RawSpecList[], SWRApiError>(
    { url: "/spec_lists" },
    fetcher,
    swrConfigOptions,
  );

  return { data: data.map(mapRawToSpecList), ...rest };
};

export const useSpecList = (id: string | null) => {
  const fetcher = useFetcher();

  const { data, ...rest } = useSWR<RawSpecList, SWRApiError>(
    id ? { url: `/spec_lists/${id}` } : null,
    fetcher,
    swrConfigOptions,
  );

  return { data: data ? mapRawToSpecList(data) : null, ...rest };
};

export const useSpecListVersion = (id?: string) => {
  const fetcher = useFetcher();

  const { data, ...rest } = useSWR<RawSpecListVersion, SWRApiError>(
    id ? { url: `/spec_lists/version/${id}` } : null,
    fetcher,
    swrConfigOptions,
  );

  return { data: data ? mapRawSpecListVersionToSpecListVersion(data, true) : null, ...rest };
};

export const useSpecListsForSpecificMask = (maskId?: string) => {
  const fetcher = useFetcher();

  const { data = [], ...rest } = useSWR<RawSpecList[], SWRApiError>(
    maskId ? { url: `/spec_lists/mask/${maskId}` } : null,
    fetcher,
    swrConfigOptions,
  );

  return { data: data.map(mapRawToSpecList), ...rest };
};

export const useCreateSpecList = () => {
  const mutater = useMutater();

  return useSWRMutation<void, SWRApiError, string, CreateSpecList>("/spec_lists", mutater);
};

export const useCreateNewSpecListVersion = () => {
  const updater = useUpdater();

  return useSWRMutation<any, SWRApiError, UpdaterParams, CreateNewSpecListVersion>(
    { url: "/spec_lists" },
    updater,
  );
};

export const useArchiveSpecList = () => {
  const mutater = useMutater();

  return useSWRMutation<void, SWRApiError, string, ArchiveSpecList>("/spec_lists/archive", mutater);
};

export const useUploadAndCreateMasterSpecList = () => {
  const uploader = useUploader();

  return useSWRMutation<void, SWRApiError, string, CreateMasterSpecList>(
    "/spec_lists/create-master-spec-list",
    uploader,
  );
};

export const useUploadAndUpdateMasterSpecList = () => {
  const uploader = useUploader();

  return useSWRMutation<void, SWRApiError, string, UpdateMasterSpecList>(
    "/spec_lists/update-master-spec-list",
    uploader,
  );
};

export const useUploadProjectSpecList = () => {
  const uploader = useUploader();

  return useSWRMutation<void, SWRApiError, string, CreateProjectSpecList>(
    "/spec_lists/create-project-spec-list",
    uploader,
  );
};

export const useUploadCustomSpecList = () => {
  const uploader = useUploader();

  return useSWRMutation<void, SWRApiError, string, CreateCustomSpecList>(
    "/spec_lists/upload-custom-spec-list",
    uploader,
  );
};

export const useValidateCustomSpecList = () => {
  const uploader = useUploader();

  return useSWRMutation<{ detail: ValidationResponse }, SWRApiError, string, CreateCustomSpecList>(
    "/spec_lists/upload-custom-spec-list",
    uploader,
  );
};

export const useUploadDesign = () => {
  const uploader = useUploader();

  return useSWRMutation<void, SWRApiError, string, UploadDesign>("/structures/upload", uploader);
};

export const useUpdateDesign = () => {
  const uploaderWithResponse = useUploaderWithResponse();

  return useSWRMutation<UploadDesignPreview | void, SWRApiError, string, UpdateDesignRequest>(
    "/structures/update",
    uploaderWithResponse,
  );
};

export const useSpecValuesAvailableForMask = (versionId?: string, maskId?: string) => {
  const {
    data: specListVersion,
    error: specListVersionError,
    isLoading: isLoadingSpecListVersion,
  } = useSpecListVersion(versionId);

  const {
    data: structures,
    error: maskStructuresError,
    isLoading: isLoadingMaskStructures,
  } = useMaskStructures(maskId);

  const isLoading = isLoadingSpecListVersion || isLoadingMaskStructures;

  const specValues: SpecValue[] = useMemo(() => {
    if (!versionId) return [];

    if (!specListVersion) return [];

    if (!structures?.structures) return [];

    const structureNames = structures.structures.map((s: any) => s.name);

    return specListVersion.specValues.filter(
      (specValue) =>
        structureNames.includes(specValue.spec.name) ||
        structureNames.includes(specValue.spec.reportName) ||
        structureNames.includes(specValue.spec.dutNames) ||
        structureNames.includes(specValue.spec.legacyNames),
    );
  }, [versionId, specListVersion, structures?.structures]);

  return { error: specListVersionError ?? maskStructuresError, isLoading, specValues };
};

export const useDownloadMasterSpec = () => {
  const { accessToken } = useAuth();

  return async (specListId: string): Promise<void> => {
    const response = await fetch(`${settings.apiBaseUrl}/spec_lists/${specListId}/download`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (!response.ok) {
      const apiError = await response.json().catch();
      const detail = apiError?.detail ?? response.statusText;
      throw new SWRApiError({ message: detail, statusCode: response.status, statusText: detail });
    }

    const filename =
      response.headers.get("Content-Disposition")?.split("filename=")[1]?.replaceAll('"', "") ??
      "master_spec";
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    downloadFileByUrl(objectUrl, filename);
  };
};
